import React, { useState, useRef } from "react";
import axios from "axios";
import { Box, Typography, Popover, Divider, CircularProgress, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HomeIcon from "@mui/icons-material/Home";
import BuildIcon from "@mui/icons-material/Build";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import FlightIcon from "@mui/icons-material/Flight";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import logger from "../utilitis/logger";

// Reusable hover-triggered order tracking timeline, shared by any screen that
// lists Priority order numbers (ORDNAME) and wants to show their delivery
// status on hover. Ported from the tracking popover in OrderList.js.

const API_URL = process.env.REACT_APP_NEO_SERVICE;

// Canonical order-fulfillment stages, in order. The tracking service only
// returns the stages that actually occurred - everything before the current
// one is treated as completed even when the service omits it.
const ALL_STATUSES = [
  { name: "קליטת הזמנה", icon: HomeIcon },
  { name: "ההזמנה בטיפול", icon: BuildIcon },
  { name: "ליקוט ואריזה", icon: LocalMallIcon },
  { name: "הוזנה למשלוח", icon: FlightIcon },
  { name: "משלוח בדרך", icon: DeliveryDiningIcon },
  { name: "משלוח הגיע", icon: CheckCircleOutlineIcon },
];

const formatTrackingDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Builds the full 6-step timeline from the raw API response. If the order
 * already reached "משלוח הגיע", only the stages that actually happened are
 * shown; otherwise the full 6-step timeline is shown with earlier stages
 * inferred as completed even when the service didn't explicitly return them.
 */
const buildTimeline = (trackingData) => {
  if (!trackingData?.steps?.length) return [];

  const steps = trackingData.steps;
  const hasDeliveryCompleted = steps.some((step) => step.status?.trim().includes("משלוח הגיע"));

  if (hasDeliveryCompleted) {
    return steps.map((step) => {
      const statusDef = ALL_STATUSES.find((s) => s.name === step.status?.trim());
      return {
        name: step.status,
        icon: statusDef ? statusDef.icon : HomeIcon,
        date: step.date,
        status: step.isCurrent ? "current" : step.isCompleted ? "completed" : "future",
      };
    });
  }

  const currentStepIndex = steps.findIndex((s) => s.isCurrent);
  const currentStatusName = currentStepIndex >= 0 ? steps[currentStepIndex].status : "";
  const stepsMap = {};
  steps.forEach((step) => {
    stepsMap[step.status] = step;
  });

  return ALL_STATUSES.map((statusDef, index) => {
    const existingStep = stepsMap[statusDef.name];
    let stepStatus = "future";

    if (existingStep) {
      stepStatus = existingStep.isCurrent ? "current" : existingStep.isCompleted ? "completed" : "future";
    } else {
      const currentIndex = ALL_STATUSES.findIndex((s) => s.name === currentStatusName);
      if (currentIndex >= 0 && index < currentIndex) {
        stepStatus = "completed";
      }
    }

    return {
      name: statusDef.name,
      icon: statusDef.icon,
      date: existingStep?.date || null,
      status: stepStatus,
    };
  });
};

/**
 * Hook that manages hover-triggered order tracking: fetches the timeline for
 * an order number and exposes everything needed to wire up hover handlers and
 * render <OrderTrackingPopover />.
 *
 * Usage:
 *   const tracking = useOrderTracking();
 *   <Chip onMouseEnter={(e) => tracking.handleHover(e, orderName)} onMouseLeave={tracking.handleLeave} />
 *   <OrderTrackingPopover {...tracking} />
 */
export const useOrderTracking = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentOrderNumber, setCurrentOrderNumber] = useState("");
  const closeTimeoutRef = useRef(null);

  const fetchTrackingData = async (orderNumber) => {
    setLoading(true);
    setError("");
    setTrackingData(null);

    try {
      const response = await axios.get(`${API_URL}/order-tracking/api/orders/${orderNumber}/track`);
      setTrackingData(response.data);
    } catch (err) {
      logger.error("Error fetching order tracking data:", err);
      setError("לא ניתן לטעון מעקב הזמנה");
    } finally {
      setLoading(false);
    }
  };

  const handleHover = (event, orderNumber) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setAnchorEl(event.currentTarget);
    setCurrentOrderNumber(orderNumber);
    fetchTrackingData(orderNumber);
  };

  const handleLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      setTrackingData(null);
      setError("");
      setCurrentOrderNumber("");
    }, 300);
  };

  const handlePopoverMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  return {
    open: Boolean(anchorEl),
    anchorEl,
    loading,
    error,
    currentOrderNumber,
    timeline: buildTimeline(trackingData),
    handleHover,
    handleLeave,
    handlePopoverMouseEnter,
  };
};

/**
 * Renders the hover popover for `useOrderTracking()`. Spread the hook's
 * return value directly onto this component's props: <OrderTrackingPopover {...tracking} />
 */
export const OrderTrackingPopover = ({
  open,
  anchorEl,
  loading,
  error,
  currentOrderNumber,
  timeline,
  handleLeave,
  handlePopoverMouseEnter,
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={handleLeave}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    disableScrollLock
    disableRestoreFocus
    sx={{ pointerEvents: "none" }}
    PaperProps={{
      sx: {
        pointerEvents: "auto",
        width: timeline.length <= 3 ? 500 : timeline.length === 4 ? 600 : timeline.length === 5 ? 700 : 800,
        maxHeight: 500,
        borderRadius: 3,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        overflow: "visible",
      },
      onMouseEnter: handlePopoverMouseEnter,
      onMouseLeave: handleLeave,
    }}
  >
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 3, textAlign: "center", color: "#295c9a", fontSize: "1.2rem" }}>
        {currentOrderNumber} : מעקב הזמנה
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      ) : timeline.length > 0 ? (
        <Box sx={{ position: "relative", pt: 4, pb: 2, direction: "rtl" }}>
          {/* Timeline line */}
          <Box
            sx={{
              position: "absolute",
              top: 70,
              left: timeline.length <= 3 ? "12%" : "8%",
              right: timeline.length <= 3 ? "12%" : "8%",
              height: 3,
              background: "rgb(223, 237, 245)",
              zIndex: 0,
            }}
          />
          {/* Progress line - RTL */}
          <Box
            sx={{
              position: "absolute",
              top: 70,
              right: timeline.length <= 3 ? "12%" : "8%",
              width: `${((timeline.findIndex((s) => s.status === "current") || 0) / (timeline.length - 1)) * (timeline.length <= 3 ? 76 : 84)}%`,
              height: 3,
              background: "linear-gradient(90deg, rgb(223, 237, 245) 0%, rgba(41, 92, 154, 0.3) 100%)",
              zIndex: 1,
              transition: "width 0.8s ease-in-out",
            }}
          />
          {/* Steps */}
          <Box sx={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 2, px: timeline.length <= 3 ? 4 : 2 }}>
            {[...timeline].reverse().map((step, index) => {
              const StepIcon = step.icon;
              return (
                <Box key={index} sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, position: "relative" }}>
                  {step.date && (
                    <Typography
                      variant="caption"
                      sx={{ position: "absolute", top: -30, color: "#295c9a", fontWeight: 700, fontSize: "0.7rem", whiteSpace: "nowrap" }}
                    >
                      {formatTrackingDate(step.date)}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      width: step.status === "current" ? 60 : 50,
                      height: step.status === "current" ? 60 : 50,
                      borderRadius: "50%",
                      background:
                        step.status === "current" ? "#65b891" : step.status === "completed" ? "#295c9a" : "rgb(223, 237, 245)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow:
                        step.status === "current"
                          ? "0 6px 20px rgba(101, 184, 145, 0.4)"
                          : step.status === "completed"
                          ? "0 3px 12px rgb(223, 237, 245)"
                          : "none",
                      transition: "all 0.3s ease",
                      animation: step.status === "current" ? "pulse 2s infinite" : "none",
                      "@keyframes pulse": { "0%, 100%": { transform: "scale(1)" }, "50%": { transform: "scale(1.05)" } },
                      border:
                        step.status === "future"
                          ? "2px solid rgba(41, 92, 154, 0.4)"
                          : step.status === "current"
                          ? "3px solid #65b891"
                          : "none",
                    }}
                  >
                    <StepIcon sx={{ color: step.status === "future" ? "#295c9a" : "white", fontSize: step.status === "current" ? 28 : 24 }} />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700, color: "#295c9a", textAlign: "center", fontSize: "0.8rem", maxWidth: 90 }}>
                    {step.name}
                  </Typography>
                  {step.status === "current" && (
                    <Box
                      sx={{
                        mt: 0.5,
                        px: 1.5,
                        py: 0.3,
                        borderRadius: 1.5,
                        background: "linear-gradient(135deg, #65b891 0%, #65b891 100%)",
                        color: "white",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                      }}
                    >
                      נוכחי
                    </Box>
                  )}
                  {step.status === "completed" && <CheckCircleIcon sx={{ mt: 0.5, color: "#295c9a", fontSize: 16 }} />}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            אין מידע זמין עבור הזמנה זו
          </Typography>
        </Box>
      )}
    </Box>
  </Popover>
);

export default OrderTrackingPopover;
